/* import __COLOCATED_TEMPLATE__ from './lsg-text-input.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { computed, observer, action } from '@ember/object';

import Component from '@ember/component';

/**
 * @type {Object}     Different types of text input styles.
 */
export const LSG_TEXT_INPUT_STYLE = {
  SINGLE_LINE_UNDER: 'single_line_under', //no top border and single line bottom border.
  ROUNDED_BORDER: 'rounded_border' //rounded border
};

/**
 * Special types of inputs which we will extend functionality to.
 *
 * @type {Object}
 */
export const LSG_TEXT_INPUT__SPECIAL_TYPES = {
  MONEY: 'money', // special type to ensure this is always float characters, only 0-9 and a decimal.
  MONEY_ALLOW_NEGATIVE: 'money_allow_negative' // same as money but allowing negative
};

export default Component.extend({

  /**
   * @inheritDoc
   */
  classNames: ['LSG-c-text-input', 'input-field'],

  /**
   * @inheritDoc
   */
  classNameBindings: [
    'isShowErrors:is-show-errors',
    '_isHasError:is-has-error',
    '_isRoundedBorderStyle:rounded-border-style'],

  /**
   * Should be an object representing the messages to show based on the various validity
   * failure properties.
   *
   * Ex: {
   *    typeMismatch: 'You must enter a valid url',
   *    valueMissing: 'You think you can just LEAVE OUT FIELDS???  Get over yourself!'
   *  }
   *
   * This object will overwrite the defaults, but will only overwrite the values that are included.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState for more info on all of the various
   * validity state options
   *
   * @type {object}
   */
  errorMessages: undefined,

  /**
   * The inputId or we'll generate a unique one.
   *
   * @param {String=}   Optional
   */
  inputId: computed(function() {
    return 'lsg-text-input-' + this.get('elementId');
  }),

  /**
   * The style of the input.
   *
   * @param {String=}    of type LSG_TEXT_INPUT_STYLE.*
   */
  inputStyle: LSG_TEXT_INPUT_STYLE.SINGLE_LINE_UNDER,

  /**
   * Signifies whether the component should show any errors that may be attributable
   * to its contents.  Generally speaking, the <input> will have error classes + :invalid/:valid pseudo-classes
   * updated on input, but you may not want to reveal these conditions until a form is submitted, etc.
   *
   * Should be passed in from the parent template, but also note that this value is set/reset locally as well
   * whenever the value changes
   *
   * @type {boolean}
   */
  isShowErrors: false,


  /**
   * Signifies whether errors should always be shown, regardless of the isShowErrors property.  Use
   * this if you'd like dynamic as-you-type form updating
   *
   * @type {boolean}
   */
  isAlwaysShowErrors: false,

  /**
   * Signifies whether the field has determined that it has an error.  It's intended to be used
   * upstream by a parent template (via {{mut}} or otherwise) and will be updated each time
   * the internal _isHasError property changes
   *
   * @type {boolean}
   */
  isHasError: false,

  /**
   * @param {Function=} Optional action on enter events
   */
  onEnter: function() {},

  /**
   * @param {Function=} Optional action on escape events
   */
  onEscape: function() {},

  /**
   * @param {Function=}    Optional action to handle when the input of the text input has changed.
   */
  onTextInputChange: function() {},

  /**
   * Add a label that will be moved over the input with the matieralize JS.
   *
   * NOTE - mutually exclusive with the placeholder property.
   *
   * @param {String}
   */
  label: '',

  /**
   * @param {String=}   Optional subText for under the text input.
   */
  subText: '',

  /**
   * The 'type' attribute of the underlying <input>.  Can be passed in from parent template.
   *
   *  Can also pass in special types: LSG_TEXT_INPUT__SPECIAL_TYPES.*
   *
   * Note: This component should generally be used for inputs that would historically just be
   * <input type="text">.  You can obviously insert anything you want here, but limiting to types
   * like 'email', 'url', 'tel', etc. probably make the most sense.
   *
   * @type {string}
   */
  type: 'text',

  /**
   * Bound to the <input>'s value.  Can be passed in from the parent template
   *
   * @type {string}
   */
  value: undefined,

  /**
   * The 'placeholder' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  placeholder: undefined,

  /**
   * The number of digits to restrict the input to.  Undefined is unlimited.
   *
   * @param {String=}
   */
  maxlength: undefined,

  /**
   * The 'name' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  name: undefined,

  /**
   * The 'disabled' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  disabled: undefined,

  /**
   * The 'readonly' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  readonly: undefined,

  /**
   * The 'required' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  required: undefined,

  /**
   * The 'pattern' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  pattern: undefined,

  /**
   * The 'autocomplete' attribute of the underlying <input>.  Can be passed in from parent template
   *
   * @type {string}
   */
  autocomplete: undefined,

  /**
   * This is the type that will be sent to the input.  We have special types which will extend on the
   * normal input's functionality.
   *
   * @type {String}
   */
  _derivedType: computed('type', function() {
    let type = this.get('type');
    if (type === LSG_TEXT_INPUT__SPECIAL_TYPES.MONEY) {
      return 'text';
    } else if (type === LSG_TEXT_INPUT__SPECIAL_TYPES.MONEY_ALLOW_NEGATIVE) {
      return 'text';
    }

    return type;
  }),

  /**
   * Tracks whether the <input> has an error as reported by its validityState
   *
   * @type {boolean}
   */
  _isHasError: false,

  /**
   * @type {Boolean}    True if this is the rounder border style text input.
   */
  _isRoundedBorderStyle: computed('inputStyle', function() {
    return this.get('inputStyle') === LSG_TEXT_INPUT_STYLE.ROUNDED_BORDER;
  }),

  /**
   * Checks the DOM for the <input> and looks at its validityState
   * to determine whether it has an error.  Updates the _isHasError
   * property with whatever it finds
   *
   * @private
   */
  _update_isHasError() {
    let $input = this.element.querySelector('input');

    // No error if the input doesn't exist
    if (!$input) {
      this.set('_isHasError', false);
    }

    let _isHasError = !($input.validity && $input.validity.valid);

    this.set('_isHasError', _isHasError);
  },

  /**
   * Computed based on whether an errorMessages object was passed-in
   * from the parent component.  Provides an array of error messages
   * to be used as a lookup table based on the type attribute of the
   * <input>.
   *
   * @type {Ember.computed | object}
   */
  _errorMessages: computed('errorMessages', function() {
    let type = this.get('type');
    let defaults = {
      valueMissing: 'This field is required.',
      typeMismatch: 'This field is formatted improperly',
      patternMismatch: 'This field is formatted improperly'
    };

    switch (type) {

      case 'email':
        defaults.typeMismatch = 'You must enter a valid email address.';
        break;

      case 'url':
        defaults.typeMismatch = 'You must enter a valid url.';
        break;
    }

    let customErrorMessages = this.get('errorMessages');

    if (customErrorMessages) {
      return Object.assign({}, defaults, customErrorMessages);
    } else {
      return defaults;
    }
  }),

  /**
   * Computed based on the accompanying _errorMessages computed property.
   * Gives an appropriate error message based on which of the validityState
   * checks the <input> element failed
   *
   * @type {Ember.computed | string}
   */
  _errorMessage: computed('_errorMessages', 'value', function() {
    let errorMessages = this.get('_errorMessages');
    let $input = this.element.querySelector('input');

    // Just return an empty string if the input isn't in the DOM
    if (!$input) {
      return '';
    }

    let validityState = $input.validity;

    // Required has highest precedence
    if (validityState.valueMissing) {
      return errorMessages.valueMissing;
    }

    // Type Mismatch is next
    if (validityState.typeMismatch) {
      return errorMessages.typeMismatch;
    }

    // Pattern match comes after that
    if (validityState.patternMismatch) {
      return errorMessages.patternMismatch;
    }

    // Just give a default error message otherwise
    return 'This field has an error.';
  }),

  /**
   * An observer that clears error visibility when the value changes
   *
   * @type {Ember.observer}
   */
  observeValue: observer('value', function() {
    // Don't do anything if the object is getting cleaned up
    if (this.get('isDestroying') || this.get('isDestroyed')) {
      return;
    }

    // Clear error visibility after input has changed
    this.set('isShowErrors', false);

    // Update the _isHasErrorProperty, taking care to wait until rendering has finished
    // so the <input> has updated validityState information
    scheduleOnce('afterRender', this, this._update_isHasError);
  }),

  /**
   * An observer that sets the public value of isHasError every time
   * the private _isHasError is calculated
   *
   * @type {Ember.observer}
   */
  observe_isHasError: observer('_isHasError', function() {
    // Don't do anything if the object is getting cleaned up
    if (this.get('isDestroying') || this.get('isDestroyed')) {
      return;
    }

    this.set('isHasError', this.get('_isHasError'));
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);

    if (this.get('type') === LSG_TEXT_INPUT__SPECIAL_TYPES.MONEY ||
    this.get('type') === LSG_TEXT_INPUT__SPECIAL_TYPES.MONEY_ALLOW_NEGATIVE) {
      scheduleOnce('afterRender', this, function() {
        let currentValue = this.get('value'),
          twoDecimalPlacesValue = parseFloat(Math.round(currentValue * 100) / 100).toFixed(2);

        if (currentValue && currentValue !== twoDecimalPlacesValue) {
          this.set('value', twoDecimalPlacesValue);
        }
      });
    } else {
      scheduleOnce('afterRender', this, function() {
        // Allow the _isHasError property to recompute, with input now in DOM
        this.notifyPropertyChange('value');
      });
    }
  },

  onInternalEnter: action(function(event) {
    this.get('onEnter')(event);
  }),

  onInternalEscapePress: action(function(event) {
    this.get('onEscape')(event);
  }),

  /**
   * Internal handler to call special functions and any user defined functions.
   *
   * @param {InputEvent} e
   */
  onInternalTextInputChange: action(function (e) {
    this.get('onTextInputChange')(e.target.value);
  }),
});
